import React from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

function formatDateLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatPercentage(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "0%";
  return `${Math.round(Number(value))}%`;
}

function computeRecommendation(metrics = {}, chartData = [], sessions = 0, t) {
  if (!sessions || sessions === 0 || !chartData || chartData.length === 0) {
    return null;
  }

  const mental =
    metrics.mental ??
    (metrics.calm || 0) -
      ((metrics.stressed || 0) +
        (metrics.anxious || 0) +
        (metrics.demotivated || 0) +
        (metrics.hurt || 0)) /
        4;

  const stressed = metrics.stressed || 0;
  const anxious = metrics.anxious || 0;
  const demotivated = metrics.demotivated || 0;
  const hurt = metrics.hurt || 0;
  const burnout = metrics.burnout || 0;

  const trend =
    chartData.length >= 2
      ? chartData[chartData.length - 1].mental - chartData[0].mental
      : 0;

  const isImproving = trend > 5;
  const isDeclining = trend < -5;

  const highNegativeEmotions = (stressed + anxious + demotivated + hurt) / 4;

  if (mental >= 80 && burnout < 30 && highNegativeEmotions < 20) {
    if (isImproving) {
      return t(
        "Excellent emotional health with positive trends. Continue maintaining healthy habits and routines.",
      );
    } else if (isDeclining) {
      return t(
        "Strong baseline but slight decline detected. Review recent stressors and maintain self-care practices.",
      );
    }
    return t(
      "Excellent emotional health. Continue maintaining positive habits and regular check-ins.",
    );
  }

  if (mental >= 70 && burnout < 40 && highNegativeEmotions < 30) {
    if (isImproving) {
      return t(
        "Good emotional stability with improving trends. Keep up the positive momentum with consistent routines.",
      );
    } else if (isDeclining) {
      return t(
        "Good baseline but declining trend. Consider stress management techniques and mindfulness practices.",
      );
    }
    return t(
      "Good stability observed. Keep consistency in emotional routines and self-care activities.",
    );
  }

  if (mental >= 50 && burnout < 50) {
    if (isImproving) {
      return t(
        "Moderate emotional state with positive improvements. Continue current strategies and consider additional support resources.",
      );
    } else if (isDeclining) {
      return t(
        "Moderate emotional state with declining trend. Increase focus on stress management, mindfulness, and consider professional guidance.",
      );
    }
    return t(
      "Moderate emotional fluctuations detected. Consider stress management, mindfulness strategies, and regular emotional check-ins.",
    );
  }

  if (mental >= 40 || burnout >= 50 || highNegativeEmotions >= 40) {
    if (isImproving) {
      return t(
        "Challenging emotional state but showing improvement. Continue current interventions and maintain close monitoring with professional support.",
      );
    } else if (isDeclining) {
      return t(
        "Significant emotional challenges with declining trend. Immediate professional support recommended along with close monitoring.",
      );
    }
    return t(
      "Significant emotional challenges detected. Recommend professional support, stress management interventions, and closer monitoring.",
    );
  }

  if (mental < 40 || burnout >= 60 || highNegativeEmotions >= 50) {
    if (isImproving) {
      return t(
        "Critical emotional state but showing improvement. Maintain intensive professional support and close monitoring.",
      );
    } else if (isDeclining) {
      return t(
        "Critical emotional decline observed. Immediate professional intervention and intensive support required.",
      );
    }
    return t(
      "Critical emotional state observed. Immediate professional support and intensive monitoring strongly recommended.",
    );
  }

  return t(
    "Emotional state requires attention. Consider professional support and closer monitoring.",
  );
}

const MentalTooltip = ({ active, payload, t }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  const delta = point.delta || 0;
  const deltaLabel =
    delta > 0
      ? `+${delta.toFixed(1)}%`
      : delta < 0
        ? `${delta.toFixed(1)}%`
        : t("No change");
  const deltaColor = delta > 0 ? "#16a34a" : delta < 0 ? "#dc2626" : "#475569";
  return (
    <div className="su-tooltip">
      <div className="su-tooltip-title">
        {point.rawDate
          ? new Date(point.rawDate).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "—"}
      </div>
      <div className="su-tooltip-row">
        <span>{t("Mental Well-being")}</span>
        <strong>{formatPercentage(point.mental)}</strong>
      </div>
      <div className="su-tooltip-row" style={{ color: deltaColor }}>
        <span>{t("Change")}</span>
        <strong>{deltaLabel}</strong>
      </div>
    </div>
  );
};

const EmotionTrendChart = ({ data, t }) => {
  if (!data?.length) {
    return <div className="su-empty">{t("No trend data available yet.")}</div>;
  }
  const latest = data[data.length - 1];
  const first = data[0];
  const trendPositive = latest && first ? latest.mental >= first.mental : true;
  const lineColor = trendPositive ? "#22c55e" : "#f97316";
  const areaGradientId = trendPositive
    ? "mentalGradientPos"
    : "mentalGradientNeg";
  return (
    <div className="su-report-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <defs>
            <linearGradient id="mentalGradientPos" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="mentalGradientNeg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#f97316" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="#E2E8F0" />
          <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickMargin={8} />
          <YAxis
            stroke="#64748B"
            domain={[0, 100]}
            tickFormatter={(val) => `${val}%`}
            fontSize={12}
          />
          <Tooltip content={<MentalTooltip t={t} />} />
          <Area
            type="monotone"
            dataKey="mental"
            stroke="none"
            fill={`url(#${areaGradientId})`}
          />
          <Line
            type="monotone"
            dataKey="mental"
            stroke={lineColor}
            strokeWidth={3}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function ReportModal({
  visible,
  onClose,
  entityType,
  range,
  data,
  loading,
  error,
  t,
}) {
  if (!visible) return null;
  const titlePrefix = entityType === "staff" ? t("Staff") : t("Student");
  const rangeLabel = range
    ? t(range.charAt(0).toUpperCase() + range.slice(1))
    : "";
  const averages = data?.summary?.average || {};
  const sessions = data?.summary?.sessions || data?.points?.length || 0;
  const chartData = (data?.points || [])
    .map((point) => {
      const rawDate = point.session_date || "";
      const calm = Number(point.calm ?? 0);
      const negatives = [
        Number(point.stressed ?? 0),
        Number(point.anxious ?? 0),
        Number(point.demotivated ?? 0),
        Number(point.hurt ?? 0),
      ].filter((v) => !Number.isNaN(v));
      const negativeAvg =
        negatives.reduce((acc, v) => acc + v, 0) /
        Math.max(1, negatives.length);
      const rawScore = calm - negativeAvg;
      const mentalScore = Math.max(0, Math.min(100, rawScore));
      return {
        name: formatDateLabel(rawDate),
        rawDate,
        mental: mentalScore,
        calm,
        stressed: Number(point.stressed ?? 0),
        burnout: Number(point.burnout ?? 0),
      };
    })
    .map((point, idx, arr) => {
      const prev = idx > 0 ? arr[idx - 1].mental : arr[idx].mental;
      const delta = idx > 0 ? point.mental - prev : 0;
      return { ...point, delta };
    });
  const avgMental = chartData.length
    ? chartData.reduce((acc, p) => acc + p.mental, 0) / chartData.length
    : 0;

  const dataPoints = data?.points || [];
  const avgCalm = dataPoints.length
    ? dataPoints.reduce((acc, p) => acc + Number(p.calm ?? 0), 0) /
      dataPoints.length
    : 0;
  const avgStressed = dataPoints.length
    ? dataPoints.reduce((acc, p) => acc + Number(p.stressed ?? 0), 0) /
      dataPoints.length
    : 0;
  const avgAnxious = dataPoints.length
    ? dataPoints.reduce((acc, p) => acc + Number(p.anxious ?? 0), 0) /
      dataPoints.length
    : 0;
  const avgDemotivated = dataPoints.length
    ? dataPoints.reduce((acc, p) => acc + Number(p.demotivated ?? 0), 0) /
      dataPoints.length
    : 0;
  const avgHurt = dataPoints.length
    ? dataPoints.reduce((acc, p) => acc + Number(p.hurt ?? 0), 0) /
      dataPoints.length
    : 0;
  const avgBurnout =
    averages.burnout ||
    (dataPoints.length
      ? dataPoints.reduce(
          (acc, p) => acc + Number(p.burnout_score ?? p.burnout ?? 0),
          0,
        ) / dataPoints.length
      : 0);

  const recommendation = computeRecommendation(
    {
      mental: avgMental,
      calm: avgCalm,
      stressed: avgStressed,
      anxious: avgAnxious,
      demotivated: avgDemotivated,
      hurt: avgHurt,
      burnout: avgBurnout,
    },
    chartData,
    sessions,
    t,
  );

  return (
    <motion.div
      className="su-report-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="su-report-modal"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="su-report-close"
          onClick={onClose}
          aria-label="Close report modal"
        >
          ×
        </button>
        <div className="su-report-modal-head">
          <h3>{`${titlePrefix} ${rangeLabel} Report`}</h3>
          <span className="su-report-date">
            {data?.start_date || "—"} &rarr; {data?.end_date || "—"}
          </span>
        </div>
        {loading && (
          <div className="su-report-loading">
            Loading {rangeLabel.toLowerCase()} insights…
          </div>
        )}
        {!loading && error && <div className="su-report-error">{error}</div>}
        {!loading && !error && (
          <>
            <EmotionTrendChart data={chartData} t={t} />
            <div className="su-report-stats">
              <div className="su-mental-wellbeing">
                <span>{t("Mental Well-being")}</span>
                <strong>{formatPercentage(avgMental)}</strong>
              </div>
              <div className="su-report-stat sessions">
                <span>{t("Sessions Count")}</span>
                <strong>{sessions}</strong>
              </div>
              <div className="su-report-stat calm">
                <span>{t("Avg Calm")}</span>
                <strong>{formatPercentage(averages.calm)}</strong>
              </div>
              <div className="su-report-stat burnout">
                <span>{t("Avg Burnout")}</span>
                <strong>{formatPercentage(averages.burnout)}</strong>
              </div>
              <div className="su-report-stat stressed">
                <span>{t("Avg Stressed")}</span>
                <strong>{formatPercentage(averages.stressed)}</strong>
              </div>
            </div>
            {recommendation && (
              <motion.div
                className="su-report-recommendation"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <strong>{t("Recommendation")}:</strong>
                <span>{recommendation}</span>
              </motion.div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
